import React from 'react';
import { Link, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faEdit } from '@fortawesome/free-solid-svg-icons';
import { Module } from '../../types/subject';
import styles from '../../subject.module.css';

interface ModuleCardProps {
    module: Module;
    subjectSlug: string;
    isStudent: boolean;
    isSuperAdmin: boolean;
    progress?: { best: number; improvement: number; attempts: number; mastered: boolean };
    showOnlyImageAndName?: boolean;
    openModuleMenuId?: string | null;
    setOpenModuleMenuId?: (id: string | null) => void;
    moduleMenuRef?: React.RefObject<HTMLDivElement | null>;
    linkTo?: string;
}

const ModuleCard: React.FC<ModuleCardProps> = ({
    module,
    subjectSlug,
    isStudent,
    isSuperAdmin,
    progress = { best: 0, improvement: 0, attempts: 0, mastered: false },
    showOnlyImageAndName = false,
    openModuleMenuId,
    setOpenModuleMenuId,
    moduleMenuRef,
    linkTo
}) => {
    const navigate = useNavigate();
    const resolvedLink = linkTo || `/subject/${subjectSlug}/${module.slug}`;

    if (showOnlyImageAndName) {
        return (
            <Link to={resolvedLink} className={styles.studentModuleCard}>
                <div className={styles.thumbnailContainer}>
                    <img
                        src={module.thumbnail_url || 'https://via.placeholder.com/300x200'}
                        alt={module.name}
                        className={styles.thumbnail}
                    />
                </div>
                <div className={styles.studentModuleCardContent}>
                    <h2 className={styles.studentModuleName}>{module.name}</h2>
                </div>
            </Link>
        );
    }

    if (isStudent) {
        return (
            <Link to={resolvedLink} className={styles.studentModuleCard}>
                <div className={styles.thumbnailContainer}>
                    <img
                        src={module.thumbnail_url || 'https://via.placeholder.com/300x200'}
                        alt={module.name}
                        className={styles.thumbnail}
                    />
                </div>
                <div className={styles.studentModuleCardContent}>
                    <h2 className={styles.studentModuleName}>{module.name}</h2>

                    <div className={styles.scoreRow}>
                        <span className={styles.bestScore}>Best: <b>{progress.best}%</b></span>
                        <span className={styles.improvement}>
                            ▲ {progress.improvement}%
                        </span>
                    </div>

                    <div className={styles.attemptsRow}>
                        <div className={styles.attemptsLabel}>
                            Attempts: <b>{progress.attempts}</b>
                        </div>
                        {progress.mastered ? (
                            <div className={styles.masteredBadge}>Mastered</div>
                        ) : (
                            <div className={styles.inProgressBadge}>In Progress</div>
                        )}
                    </div>
                </div>
            </Link>
        );
    }

    return (
        <div className={styles.card}>
            <div className={styles.thumbnailContainer}>
                <img
                    src={module.thumbnail_url || 'https://via.placeholder.com/300x200'}
                    alt={module.name}
                    className={styles.thumbnail}
                />
            </div>
            <div className={styles.cardContent}>
                <div style={{ marginBottom: '1rem' }}>
                    <div className={styles.cardHeader}>
                        <h2 className={styles.cardName}>{module.name}</h2>
                        {isSuperAdmin && setOpenModuleMenuId && (
                            <div
                                className={styles.optionMenu}
                                ref={openModuleMenuId === module.id ? moduleMenuRef : null}
                            >
                                <button
                                    className={styles.menuButton}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setOpenModuleMenuId(openModuleMenuId === module.id ? null : module.id);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faEllipsisV} />
                                </button>
                                {openModuleMenuId === module.id && (
                                    <div className={styles.dropdownContent} style={{ display: 'block' }}>
                                        <button
                                            className={styles.dropdownItem}
                                            onClick={() => navigate(`/subject/${subjectSlug}/edit-module/${module.slug}`)}
                                        >
                                            <FontAwesomeIcon icon={faEdit} /> Edit
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <p className={styles.cardDescription}>{module.short_description}</p>
                </div>

                <Link
                    to={resolvedLink}
                    className={styles.button}
                >
                    View More
                </Link>
            </div>
        </div>
    );
};

export default ModuleCard;
